import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import {
  addDoc,
  arrayUnion,
  collection,
  doc,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  onSnapshot,
} from 'firebase/firestore';
import { db, functions } from '../../firebase';
import { appendTicketLog, participantsForTicket } from '../../lib/ticketLogs';
import { appendTicketInboxAlert } from '../../lib/ticketAlerts';
import {
  isRichTextEmpty,
  plainTextToHtml,
} from '../../lib/richText';
import {
  commentAuthorLabel,
  buildLastCommentTicketPatch,
} from '../../lib/ticketCommentMeta';
import { useCompanyAiAgents } from '../../hooks/useCompanyAiAgents';
import { useCompanyUsers } from '../../hooks/useCompanyUsers';
import { useCompanyUserGroups } from '../../hooks/useCompanyUserGroups';
import {
  buildTicketMentionItems,
  extractMentionsFromCommentPayload,
  commentIsAiDirected,
} from '../../lib/ticketMentions';
import { isTerminalTicketStatus } from '../../lib/ticketStatus';
import TicketCommentEditor from './TicketCommentEditor';
import RichTextDisplay from '../rich-text/RichTextDisplay';

function terminalAiCommentMessage(status) {
  const label =
    typeof status === 'string' && status.trim().toLowerCase() === 'closed'
      ? 'Closed'
      : 'Resolved';
  return `This ticket is ${label}. Reopen it first to continue the conversation with the AI agent.`;
}

function parseDate(v) {
  if (!v) return null;
  if (v.toDate) return v.toDate();
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

function isDraftEmpty(draft) {
  return isRichTextEmpty(draft);
}

function isAiOrSystemComment(comment) {
  if (!comment) return true;
  if (comment.isAi === true || comment.isAiResponse === true) return true;
  if (comment.senderType === 'agent' || comment.senderType === 'system') return true;
  const authorUid = typeof comment.authorUid === 'string' ? comment.authorUid : '';
  return authorUid.startsWith('ai_agent:') || authorUid.startsWith('system:');
}

function canEditHumanComment(comment, actorUid, actorRole) {
  if (!comment || !actorUid || isAiOrSystemComment(comment)) return false;
  if (comment.authorUid === actorUid) return true;
  return actorRole === 'company_admin' || actorRole === 'super_admin';
}

/**
 * Real-time commentary on a ticket (`tickets/{ticketId}/comments`).
 */
export default function TicketCommentsThread({
  ticketId,
  companyId,
  ticketTitle,
  canPost,
  actorUid,
  actorEmail,
  actorRole,
  actorDisplayName,
  userData,
  participantTicket,
}) {
  const [comments, setComments] = useState([]);
  const [draft, setDraft] = useState('');
  const [posting, setPosting] = useState(false);
  const [draftingReply, setDraftingReply] = useState(false);
  const [draftError, setDraftError] = useState('');
  const [focusAtEndToken, setFocusAtEndToken] = useState(0);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editDraft, setEditDraft] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState('');
  const listRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { agents: companyAgents } = useCompanyAiAgents(companyId);
  const { users: companyUsers } = useCompanyUsers(companyId);
  const { groups: companyGroups } = useCompanyUserGroups(companyId);

  const agentById = useMemo(() => {
    const map = {};
    (companyAgents || []).forEach((agent) => {
      if (agent.id) map[agent.id] = agent;
    });
    return map;
  }, [companyAgents]);

  const mentionItems = useMemo(() => {
    const humans = companyUsers || [];
    const aiUsers = humans.filter((user) => user.isAi === true);
    const humanUsers = humans.filter((user) => user.isAi !== true);
    return buildTicketMentionItems(
      humanUsers,
      aiUsers,
      companyAgents || [],
      companyGroups || [],
    );
  }, [companyUsers, companyAgents, companyGroups]);

  const sortedComments = useMemo(() => {
    return [...comments].sort((a, b) => {
      const ta = parseDate(a.createdAt)?.getTime() ?? 0;
      const tb = parseDate(b.createdAt)?.getTime() ?? 0;
      if (tb !== ta) return tb - ta;
      return String(b.id || '').localeCompare(String(a.id || ''));
    });
  }, [comments]);

  useEffect(() => {
    if (!listRef.current) return;
    listRef.current.scrollTop = 0;
  }, [ticketId, sortedComments.length]);

  useEffect(() => {
    const prefill = location.state?.feedbackPrefill;
    if (typeof prefill !== 'string' || !prefill.trim() || !ticketId) return;

    setDraft(plainTextToHtml(prefill));
    setFocusAtEndToken((token) => token + 1);
    navigate(location.pathname + location.search, { replace: true, state: {} });
  }, [location.state, location.pathname, location.search, navigate, ticketId]);

  useEffect(() => {
    if (!ticketId) {
      setComments([]);
      return undefined;
    }
    const q = query(
      collection(db, 'tickets', ticketId, 'comments'),
      orderBy('createdAt', 'desc'),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setComments(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      (err) => {
        console.error('TicketCommentsThread:', err);
      },
    );
    return () => unsub();
  }, [ticketId]);

  const cancelEdit = () => {
    setEditingCommentId(null);
    setEditDraft('');
    setEditError('');
  };

  const startEdit = (comment) => {
    if (!canEditHumanComment(comment, actorUid, actorRole)) return;
    setEditingCommentId(comment.id);
    setEditDraft(typeof comment.body === 'string' ? comment.body : '');
    setEditError('');
  };

  const saveEdit = async (comment) => {
    if (
      !ticketId ||
      !companyId ||
      !actorUid ||
      !comment?.id ||
      isDraftEmpty(editDraft) ||
      !canEditHumanComment(comment, actorUid, actorRole)
    ) {
      return;
    }

    const editMentions = extractMentionsFromCommentPayload(editDraft, 'html', []);
    if (
      isTerminalTicketStatus(participantTicket?.status) &&
      commentIsAiDirected(participantTicket, editMentions)
    ) {
      setEditError(terminalAiCommentMessage(participantTicket?.status));
      return;
    }

    const prevBody = typeof comment.body === 'string' ? comment.body : '';
    const prevFormat =
      typeof comment.bodyFormat === 'string' ? comment.bodyFormat : 'html';
    if (prevBody === editDraft && prevFormat === 'html') {
      cancelEdit();
      return;
    }

    setSavingEdit(true);
    setEditError('');
    try {
      const mentions = editMentions;
      const prevWakeGen = Math.max(
        0,
        Math.floor(Number(comment.aiAgentWakeGeneration) || 0),
      );
      const nextWakeGen = prevWakeGen + 1;
      const historyEntry = {
        body: prevBody,
        bodyFormat: prevFormat,
        editedAt: new Date().toISOString(),
        editedByUid: actorUid,
        wakeGeneration: prevWakeGen,
      };

      await updateDoc(doc(db, 'tickets', ticketId, 'comments', comment.id), {
        body: editDraft,
        bodyFormat: 'html',
        mentions,
        editedAt: serverTimestamp(),
        editedByUid: actorUid,
        aiAgentWakeGeneration: nextWakeGen,
        editHistory: arrayUnion(historyEntry),
      });

      const lastCommentPatch = buildLastCommentTicketPatch(
        {
          body: editDraft,
          bodyFormat: 'html',
          authorUid: comment.authorUid,
          authorDisplayName: comment.authorDisplayName,
          authorEmail: comment.authorEmail,
          isAi: false,
          createdAt: new Date(),
        },
        agentById,
      );
      await updateDoc(doc(db, 'tickets', ticketId), {
        updatedAt: serverTimestamp(),
        updatedBy: actorUid,
        ...lastCommentPatch,
        lastCommentAt: serverTimestamp(),
      });

      const t = participantTicket || {};
      await appendTicketLog(db, {
        companyId,
        ticketId,
        action: 'ticket_comment_edited',
        summary: `${actorDisplayName || actorEmail || actorUid} edited a comment`,
        meta: {
          commentId: comment.id,
          wakeGeneration: nextWakeGen,
          preview: lastCommentPatch.lastCommentPreview,
        },
        actorUid,
        actorEmail: actorEmail || '',
        actorRole: actorRole || '',
        participantUids: participantsForTicket(
          { reporterUid: t.reporterUid, assigneeUid: t.assigneeUid },
          actorUid,
        ),
      });

      cancelEdit();
    } catch (e) {
      console.error('save comment edit:', e);
      setEditError(e.message || 'Could not save the edit.');
    } finally {
      setSavingEdit(false);
    }
  };

  const post = async () => {
    if (!ticketId || !companyId || !actorUid || isDraftEmpty(draft) || !canPost) return;
    const mentions = extractMentionsFromCommentPayload(draft, 'html', []);
    if (
      isTerminalTicketStatus(participantTicket?.status) &&
      commentIsAiDirected(participantTicket, mentions)
    ) {
      setDraftError(terminalAiCommentMessage(participantTicket?.status));
      return;
    }
    setPosting(true);
    setDraftError('');
    try {
      const commentPayload = {
        body: draft,
        bodyFormat: 'html',
        authorUid: actorUid,
        authorEmail: actorEmail || '',
        authorDisplayName: actorDisplayName || actorEmail || actorUid,
        authorRole: actorRole || '',
        mentions,
        aiAgentWakeGeneration: 0,
        createdAt: serverTimestamp(),
      };
      await addDoc(collection(db, 'tickets', ticketId, 'comments'), commentPayload);
      const lastCommentPatch = buildLastCommentTicketPatch(
        {
          ...commentPayload,
          createdAt: new Date(),
        },
        agentById,
      );
      await updateDoc(doc(db, 'tickets', ticketId), {
        updatedAt: serverTimestamp(),
        updatedBy: actorUid,
        ...lastCommentPatch,
        lastCommentAt: serverTimestamp(),
      });
      const t = participantTicket || {};
      const preview = lastCommentPatch.lastCommentPreview;
      await appendTicketLog(db, {
        companyId,
        ticketId,
        action: 'ticket_commented',
        summary: `${actorDisplayName || actorEmail || actorUid} added a comment`,
        meta: { preview, mentionCount: mentions.length },
        actorUid,
        actorEmail: actorEmail || '',
        actorRole: actorRole || '',
        participantUids: participantsForTicket(
          { reporterUid: t.reporterUid, assigneeUid: t.assigneeUid },
          actorUid,
        ),
      });
      try {
        await appendTicketInboxAlert(db, {
          companyId,
          ticketId,
          title: `Comment: ${ticketTitle || 'Ticket'}`,
          message: `${actorDisplayName || actorEmail || actorUid} commented.`,
          severity: 'info',
          ruleCode: 'TICKET_COMMENT',
          ticketAction: 'comment',
          notifySuperAdmins: Boolean(t.platformEscalation),
          actorUid,
          actorEmail: actorEmail || '',
        });
      } catch (e) {
        console.warn('appendTicketInboxAlert:', e);
      }
      setDraft('');
    } catch (e) {
      console.error('post comment:', e);
      setDraftError(e.message || 'Could not post the comment.');
    } finally {
      setPosting(false);
    }
  };

  const draftReplyWithAi = async () => {
    if (!ticketId || !companyId || !canPost || draftingReply) return;
    if (
      isTerminalTicketStatus(participantTicket?.status) &&
      commentIsAiDirected(participantTicket, [])
    ) {
      setDraftError(terminalAiCommentMessage(participantTicket?.status));
      return;
    }
    setDraftingReply(true);
    setDraftError('');
    try {
      const fn = httpsCallable(functions, 'generateTicketReply');
      const result = await fn({ ticketId, companyId });
      const reply = result.data?.draft;
      if (typeof reply === 'string' && reply.trim()) {
        setDraft(plainTextToHtml(reply.trim()));
      } else {
        setDraftError('No draft reply was returned. Try again.');
      }
    } catch (e) {
      console.error('draftReplyWithAi:', e);
      setDraftError(e.message || 'Could not draft a reply with AI.');
    } finally {
      setDraftingReply(false);
    }
  };

  const ticketIsTerminal = isTerminalTicketStatus(participantTicket?.status);
  const draftMentions = useMemo(
    () => extractMentionsFromCommentPayload(draft, 'html', []),
    [draft],
  );
  const editMentionsLive = useMemo(
    () => extractMentionsFromCommentPayload(editDraft, 'html', []),
    [editDraft],
  );
  const blockAiPost =
    ticketIsTerminal && commentIsAiDirected(participantTicket, draftMentions);
  const blockAiEdit =
    ticketIsTerminal &&
    commentIsAiDirected(participantTicket, editMentionsLive);
  const blockAiDraftReply =
    ticketIsTerminal && commentIsAiDirected(participantTicket, []);
  const terminalBlockMessage = terminalAiCommentMessage(
    participantTicket?.status,
  );

  return (
    <Box>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
        Discussion
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        Newest comments appear first. Editing a comment can re-prompt the assigned AI agent;
        pending code sandboxes are left unchanged.
      </Typography>
      {canPost ? (
        <Stack spacing={1} sx={{ mb: 1.5 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Button
              variant="outlined"
              size="small"
              onClick={draftReplyWithAi}
              disabled={
                posting ||
                draftingReply ||
                Boolean(editingCommentId) ||
                blockAiDraftReply
              }
              startIcon={draftingReply ? <CircularProgress size={16} /> : null}
            >
              {draftingReply ? 'Drafting…' : '✨ Draft Reply with AI'}
            </Button>
          </Stack>
          {blockAiPost || blockAiDraftReply ? (
            <Alert severity="info">{terminalBlockMessage}</Alert>
          ) : null}
          {draftError && (
            <Alert severity="error" onClose={() => setDraftError('')}>
              {draftError}
            </Alert>
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
            <Box sx={{ flex: 1, width: '100%' }}>
              <TicketCommentEditor
                value={draft}
                onChange={setDraft}
                mentionItems={mentionItems}
                disabled={posting || draftingReply || Boolean(editingCommentId)}
                minRows={2}
                helperText="Type @ to mention a teammate, AI worker, or group."
                focusAtEndToken={focusAtEndToken}
              />
            </Box>
            <Button
              variant="contained"
              onClick={post}
              disabled={
                posting ||
                draftingReply ||
                isDraftEmpty(draft) ||
                Boolean(editingCommentId) ||
                blockAiPost
              }
              sx={{ flexShrink: 0 }}
            >
              {posting ? 'Posting…' : 'Post'}
            </Button>
          </Stack>
        </Stack>
      ) : (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          You can view this thread but cannot post comments on this ticket.
        </Typography>
      )}
      <Paper
        ref={listRef}
        variant="outlined"
        sx={{
          p: 1.5,
          maxHeight: 360,
          overflow: 'auto',
          bgcolor: 'action.hover',
        }}
      >
        {sortedComments.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No comments yet. Be the first to reply.
          </Typography>
        ) : (
          <Stack spacing={1.5} divider={<Divider flexItem />}>
            {sortedComments.map((c) => {
              const when = parseDate(c.createdAt);
              const editedWhen = parseDate(c.editedAt);
              const who = commentAuthorLabel(c, agentById);
              const editable = canEditHumanComment(c, actorUid, actorRole);
              const isEditing = editingCommentId === c.id;
              return (
                <Box key={c.id}>
                  <Stack direction="row" justifyContent="space-between" alignItems="baseline" gap={1} flexWrap="wrap">
                    <Typography variant="subtitle2" component="span">
                      {who}
                      {(c.isAiResponse || c.isAi || c.senderType === 'agent') && (
                        <Typography component="span" variant="caption" color="secondary" sx={{ ml: 0.75 }}>
                          · AI
                        </Typography>
                      )}
                      {editedWhen && (
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                          · edited
                        </Typography>
                      )}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {editable && !isEditing && (
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => startEdit(c)}
                          disabled={savingEdit || Boolean(editingCommentId)}
                          sx={{ minWidth: 0, px: 0.5 }}
                        >
                          Edit
                        </Button>
                      )}
                      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                        {when ? when.toLocaleString() : '…'}
                      </Typography>
                    </Stack>
                  </Stack>
                  {c.authorRole && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {c.authorRole}
                    </Typography>
                  )}
                  {isEditing ? (
                    <Stack spacing={1} sx={{ mt: 0.75 }}>
                      {blockAiEdit ? (
                        <Alert severity="info">{terminalBlockMessage}</Alert>
                      ) : null}
                      {editError && (
                        <Alert severity="error" onClose={() => setEditError('')}>
                          {editError}
                        </Alert>
                      )}
                      <TicketCommentEditor
                        value={editDraft}
                        onChange={setEditDraft}
                        mentionItems={mentionItems}
                        disabled={savingEdit}
                        minRows={2}
                        helperText="Save to update the comment. If an AI agent is still assigned, this can re-prompt them."
                      />
                      <Stack direction="row" spacing={1}>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => saveEdit(c)}
                          disabled={
                            savingEdit ||
                            isDraftEmpty(editDraft) ||
                            blockAiEdit
                          }
                        >
                          {savingEdit ? 'Saving…' : 'Save & re-prompt'}
                        </Button>
                        <Button
                          variant="text"
                          size="small"
                          onClick={cancelEdit}
                          disabled={savingEdit}
                        >
                          Cancel
                        </Button>
                      </Stack>
                    </Stack>
                  ) : (
                    <Box sx={{ mt: 0.5 }}>
                      <RichTextDisplay body={c.body} bodyFormat={c.bodyFormat || 'html'} />
                    </Box>
                  )}
                </Box>
              );
            })}
          </Stack>
        )}
      </Paper>
    </Box>
  );
}
